import * as R from 'ramda';
import * as m from './memoize';
import { AnyFunc } from './typeHelper';

export type UnboxPromise<T> = T extends PromiseLike<infer R> ? R : never;

export function memoize<Args extends any[], Ret extends Promise<any>>(
  fn: AnyFunc<Args, Ret>,
): (...args: Args) => Ret {
  const cacheMap = new m.memoize.DefaultCacheMap<Args, Ret>();
  const memoizedFn = m.memoize(fn, { cache: cacheMap });

  return (...args) => {
    const res = memoizedFn(...args);
    void res.catch(() => {
      cacheMap.delete(args);
    });
    return res;
  };
}

export function memorizeWhileInFlight<
  Args extends any[],
  Ret extends Promise<any>,
>(fn: AnyFunc<Args, Ret>): (...args: Args) => Ret {
  const cacheMap = new m.memoize.DefaultCacheMap<Args, Ret>();
  const memoizedFn = m.memoize(fn, { cache: cacheMap });

  return (...args) => {
    const res = memoizedFn(...args);
    void res.finally(() => {
      cacheMap.delete(args);
    });
    return res;
  };
}

export function throttle<Args extends any[], Ret>(
  fn: AnyFunc<Args, Promise<Ret>>,
  wait: number,
): (...args: Args) => Promise<Ret> {
  let callInfo:
    | undefined
    | {
        timer: number;
        latestArgs: Args;
        defer: defer.Deferred<Ret>;
      };

  return (...args) => {
    if (callInfo != null) {
      callInfo.latestArgs = args;
    } else {
      callInfo = {
        defer: defer(),
        latestArgs: args,
        timer: setTimeout(() => {
          if (callInfo == null) return;
          const promise = fn(...callInfo.latestArgs);
          void promise.then(callInfo.defer.resolve, callInfo.defer.reject);
          callInfo = undefined;
        }, wait),
      };
    }
    return callInfo.defer.promise;
  };
}

export function throttleLeading<Args extends any[], Ret>(
  fn: AnyFunc<Args, Promise<Ret>>,
  wait: number,
): (...args: Args) => Promise<Ret> {
  let callInfo:
    | undefined
    | {
        timer: number;
        latestArgs: Args;
        defer: defer.Deferred<Ret>;
      };

  return (...args) => {
    if (callInfo != null) {
      callInfo.latestArgs = args;
    } else {
      callInfo = {
        defer: defer(),
        latestArgs: args,
        timer: setTimeout(() => {
          callInfo = undefined;
        }, wait),
      };

      void (async () => {
        const promise = fn(...callInfo.latestArgs);
        void promise.then(callInfo.defer.resolve, callInfo.defer.reject);
      })();
    }
    return callInfo.defer.promise;
  };
}

export async function objAll<I extends Record<string, Promise<any>>>(
  inputs: I,
): Promise<{ [K in keyof I]: UnboxPromise<I[K]> }> {
  const res = await Promise.all(Object.values(inputs));
  return R.zipObj(Object.keys(inputs), res) as any;
}

export function defer<T = void>(): defer.Deferred<T> {
  let resolve: defer.Deferred<T>['resolve'];
  let reject: defer.Deferred<T>['reject'];
  const promise: defer.Deferred<T>['promise'] = new Promise<T>(
    (_resolve, _reject) => {
      resolve = _resolve;
      reject = _reject;
    },
  );
  return {
    promise,
    resolve: resolve!,
    reject: reject!,
  };
}
export namespace defer {
  export interface Deferred<T> {
    promise: Promise<T>;
    resolve: (value: T) => void;
    reject: (err: any) => void;
  }
}

/**
 * Like `Array.prototype.every`, but for `Promise<T>[]`
 *
 * It only need wait first `falsy` value, so it may resolve faster than
 * `Promise.all(...).then(every(identity))`
 */
export function every<T>(
  predicate: (item: T) => boolean,
  array: Promise<T>[],
): Promise<boolean> {
  const deferred = defer<boolean>();

  array.forEach(p => {
    void p.then(v => {
      const result = predicate(v);
      if (!result) deferred.resolve(false);
    }, deferred.reject);
  });

  void Promise.all(array).then(() => deferred.resolve(true));

  return deferred.promise;
}

/**
 * Like `Array.prototype.some`, but for `Promise<T>[]`
 *
 * It only need wait first `truthy` value, so it may resolve faster than
 * `Promise.all(...).then(some(identity))`
 */
export function some<T>(
  predicate: (item: T) => boolean,
  array: Promise<T>[],
): Promise<boolean> {
  const deferred = defer<boolean>();

  array.forEach(p => {
    void p.then(v => {
      const result = predicate(v);
      if (result) deferred.resolve(true);
    }, deferred.reject);
  });

  void Promise.all(array).then(() => deferred.resolve(false));

  return deferred.promise;
}
